import './caseStudy.css'
import Footer from "./footer"
import RevealTitle from "./RevealTitle"
import gale from "../Images/galeCover.jpg"

const INGEST = ["Medical book PDF", "Extract text", "Chunk", "Embed · MiniLM", "Pinecone"];
const RAGFLOW = ["User question", "Retrieve top-k chunks", "LLM generates", "Grounded answer"];
const VOICE = ["Voice in", "Whisper · STT", "RAG chatbot", "pyttsx3 · TTS", "Voice out"];

const QUERY_STEPS = [
    { n: "01", h: "The query goes in", b: "A question enters the RAG chain.", code: `rag_chain.invoke({"input": "what is Acromegaly and gigantism?"})` },
    { n: "02", h: "Retriever searches Pinecone", b: "The query is embedded with the same MiniLM model, then Pinecone returns the 3 most similar chunks from the medical text.", code: `docsearch.as_retriever(search_type="similarity", search_kwargs={"k": 3})` },
    { n: "03", h: "Chunks are stuffed into the prompt", b: "A stuff-documents chain drops the 3 retrieved chunks into {context} inside the system prompt.", code: `system_prompt = ("You are a Medical assistant..." "{context}")` },
    { n: "04", h: "The LLM answers", b: "LLaMA (served through OpenRouter) receives the full, grounded prompt and generates the reply.", code: null },
    { n: "05", h: "You get back the answer + its sources", b: "The response carries both the generated text and the exact chunks it was built from.", code: `response["answer"]   ·   response["context"]` },
];

const STACK = [
    { title: "Core", items: ["Python", "LangChain"] },
    { title: "Retrieval", items: ["Pinecone", "MiniLM embeddings", "k = 3 similarity"] },
    { title: "LLM", items: ["LLaMA", "OpenRouter"] },
    { title: "Voice & UI", items: ["Whisper", "pyttsx3", "Chainlit"] },
];

export default function Medchat() {
    return (
        <>
            <div className="cs-wrap">
                <header className="cs-hero">
                    <p className="cs-eyebrow">Case study · AI · Etisalat</p>
                    <RevealTitle text="MedChat" className="cs-title" />
                    <p className="cs-subtitle">
                        A voice-enabled medical RAG chatbot — grounded answers from a trusted medical reference, over LangChain, Pinecone, and Whisper.
                    </p>
                </header>

                <div className="cs-links">
                    <a className="cs-linkBtn" href="https://github.com/mohammed0Swalih/medChatBot_Swalih" target="_blank" rel="noreferrer">GitHub ↗</a>
                </div>

                <div className="cs-cover">
                    <div className="cs-ph cs-ph--cover">Chainlit chat UI — screenshot (coming)</div>
                </div>

                <div className="cs-meta">
                    <div><p className="cs-meta-label">Role</p><p className="cs-meta-value">AI &amp; RAG (solo)</p></div>
                    <div><p className="cs-meta-label">Context</p><p className="cs-meta-value">Etisalat internship</p></div>
                    <div><p className="cs-meta-label">Type</p><p className="cs-meta-value">Voice RAG chatbot</p></div>
                    <div><p className="cs-meta-label">Interface</p><p className="cs-meta-value">Chainlit · voice I/O</p></div>
                </div>

                {/* Overview */}
                <section className="cs-section">
                    <h2 className="cs-label">Overview</h2>
                    <p className="cs-statement">LLMs hallucinate — dangerous for medical questions.</p>
                    <p className="cs-body">
                        A plain LLM will confidently invent medical facts. MedChat fixes that with <strong>retrieval-augmented generation</strong>:
                        every answer is grounded in chunks retrieved from a real medical reference, and the response carries the exact
                        source passages it used — so it's checkable, not guesswork.
                    </p>
                    <div className="cs-flow">
                        {RAGFLOW.map((s, i, a) => (
                            <span className="cs-flow-item" key={s}>{s}{i < a.length - 1 && <span className="cs-flow-arrow">→</span>}</span>
                        ))}
                    </div>
                </section>

                {/* Ingestion */}
                <section className="cs-section">
                    <h2 className="cs-label">Building the knowledge base</h2>
                    <p className="cs-statement">From a medical textbook to a searchable vector index.</p>
                    <div className="cs-flow">
                        {INGEST.map((s, i, a) => (
                            <span className="cs-flow-item" key={s}>{s}{i < a.length - 1 && <span className="cs-flow-arrow">→</span>}</span>
                        ))}
                    </div>
                    <p className="cs-body">
                        The knowledge base is built from <strong>The Gale Encyclopedia of Medicine</strong> — a trusted medical
                        reference. The source is extracted, split into token-sized chunks, embedded with a <code>MiniLM</code> model,
                        and the resulting vectors are stored in <code>Pinecone</code> as the index the chatbot searches at query time.
                    </p>
                    <div className="cs-source">
                        <img className="cs-source-cover" src={gale} alt="The Gale Encyclopedia of Medicine — cover" />
                        <div className="cs-source-info">
                            <p className="cs-source-label">Knowledge source</p>
                            <p className="cs-source-title">The Gale Encyclopedia of Medicine</p>
                            <p className="cs-source-meta">Second Edition · Volume 1</p>
                        </div>
                    </div>
                </section>

                {/* Query walkthrough */}
                <section className="cs-section">
                    <h2 className="cs-label">What happens on a query</h2>
                    <p className="cs-statement">Question in, grounded answer plus its sources out.</p>
                    <div className="cs-steps">
                        {QUERY_STEPS.map((s) => (
                            <div className="cs-step" key={s.n}>
                                <span className="cs-step-n">{s.n}</span>
                                <div>
                                    <h4 className="cs-item-head">{s.h}</h4>
                                    <p className="cs-item-body">{s.b}</p>
                                    {s.code && <p className="cs-body" style={{ margin: '10px 0 0' }}><code>{s.code}</code></p>}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Voice */}
                <section className="cs-section">
                    <h2 className="cs-label">Voice capability</h2>
                    <p className="cs-statement">Ask out loud, hear it back.</p>
                    <div className="cs-flow">
                        {VOICE.map((s, i, a) => (
                            <span className="cs-flow-item" key={s}>{s}{i < a.length - 1 && <span className="cs-flow-arrow">→</span>}</span>
                        ))}
                    </div>
                    <p className="cs-body">
                        <code>Whisper</code> transcribes the spoken question, the RAG chain answers it, and <code>pyttsx3</code> speaks the reply back —
                        an offline TTS, so the voice loop works without a cloud call.
                    </p>
                </section>

                {/* Stack */}
                <section className="cs-section">
                    <h2 className="cs-label">Tech stack</h2>
                    <div className="cs-stackGroups">
                        {STACK.map((g) => (
                            <div className="cs-stackGroup" key={g.title}>
                                <h4 className="cs-stackTitle">{g.title}</h4>
                                <div className="cs-chips">{g.items.map((it) => <span className="cs-chip" key={it}>{it}</span>)}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Delivered + roadmap */}
                <section className="cs-section">
                    <h2 className="cs-label">What I built &amp; what's next</h2>
                    <div className="cs-cols">
                        <div className="cs-card">
                            <h3 className="cs-card-title">Delivered</h3>
                            <ul className="cs-list">
                                <li>A full RAG pipeline over a medical reference (chunk → embed → Pinecone).</li>
                                <li>Top-3 similarity retrieval with MiniLM embeddings.</li>
                                <li>Grounded answers from LLaMA that return their source chunks.</li>
                                <li>A voice loop: Whisper (STT) + pyttsx3 (offline TTS).</li>
                                <li>A conversational Chainlit interface.</li>
                            </ul>
                        </div>
                        <div className="cs-card">
                            <h3 className="cs-card-title">Roadmap</h3>
                            <ul className="cs-list">
                                <li>Show cited source passages inline in the UI.</li>
                                <li>Safety guardrails + a clear "not medical advice" disclaimer.</li>
                                <li>A larger, multi-book medical corpus.</li>
                                <li>Streaming responses and cloud deployment.</li>
                            </ul>
                        </div>
                    </div>
                </section>
            </div>

            <Footer />
        </>
    )
}
